import React from "react";
import PropTypes from "prop-types";
import Box from "@mui/material/Box";
import Collapse from "@mui/material/Collapse";
import IconButton from "@mui/material/IconButton";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Typography from "@mui/material/Typography";
import Paper from "@mui/material/Paper";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import useFetchObjects from "../../api/useFetchObjects";
import { useAuth } from "../../context/AuthContext";
import { useTranslation } from "react-i18next";
import { Button, Divider, Grid, Menu, Pagination, Select } from "@mui/material";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import dayjs from "dayjs";

import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";

import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import { exportSaleReturnsToPDF } from "../../utils/exportSaleReturn";
import LinearProgress from "@mui/material/LinearProgress";
import RefreshIcon from "@mui/icons-material/Refresh";
import { formatJalaliDate } from "../utils/dateFormat";

import { JalaliDatePickerField } from "../Components/JalaliDateField";
const firstCurrencyValue = (obj) => {
  if (obj && typeof obj === "object") {
    const firstEntry = Object.values(obj)[0];
    if (firstEntry && typeof firstEntry === "object")
      return firstEntry.source ?? "-";
    return firstEntry ?? "-";
  }
  return obj ?? "-";
};

function Row({ row }) {
  const { t } = useTranslation();
  const [open, setOpen] = React.useState(false);
  const itemCount = row.return_items?.length ?? 0;

  const currency = row.sale_details
    ? Object.keys(row.sale_details.total_amount || {})[0] || ""
    : "";

  return (
    <>
      <TableRow sx={{ "& > *": { borderBottom: "unset" } }}>
        <TableCell>
          <IconButton size="small" onClick={() => setOpen(!open)}>
            {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </TableCell>

        <TableCell>{row.sale_details?.id ? `Sale #${row.sale_details.id}` : "-"}</TableCell>
        <TableCell>{formatJalaliDate(row.return_date)}</TableCell>
        <TableCell>{row.reason ?? "-"}</TableCell>
        <TableCell>
          {firstCurrencyValue(row.sale_details?.total_amount)} {currency}
        </TableCell>
        <TableCell>
          {row.total_refound_amount ?? "-"} {currency}
        </TableCell>
        <TableCell>{itemCount}</TableCell>
      </TableRow>

      <TableRow>
        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={7}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box sx={{ m: 1 }}>
              <Typography variant="h6" gutterBottom component="div">
                {t("ReportIndex.returnSale_collapse_title")}
              </Typography>

              <Table size="small" aria-label="return-items">
                <TableHead>
                  <TableRow>
                    <TableCell>
                      {t("ReportIndex.returnSale_collapse_product")}
                    </TableCell>
                    <TableCell>
                      {t("ReportIndex.returnSale_collapse_qty")}
                    </TableCell>
                    <TableCell>
                      {t("ReportIndex.returnSale_collapse_price")}
                    </TableCell>
                    <TableCell>
                      {t("ReportIndex.returnSale_collapse_refundAmount")}
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {row.return_items?.map((item) => {
                    const salePrice = firstCurrencyValue(
                      item.sale_item_details?.sale_price
                    );
                    return (
                      <TableRow key={item.id}>
                        <TableCell component="th" scope="row">
                          {item.sale_item_details?.product?.name}
                        </TableCell>
                        <TableCell>{item.return_quantity}</TableCell>
                        <TableCell>{salePrice}</TableCell>
                        <TableCell>{item.refund_amount}</TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
}

Row.propTypes = {
  row: PropTypes.shape({
    sale_details: PropTypes.object,
    return_date: PropTypes.string,
    reason: PropTypes.string,
    total_refound_amount: PropTypes.string,
    return_items: PropTypes.array,
  }).isRequired,
};

export default function ReturnTable({ selectedBranch = "" }) {
  const auth = useAuth();
  const token = auth?.user?.token;
  const { t } = useTranslation();
  const allCustomersOption = React.useMemo(
    () => ({ id: "", name: t("ReportIndex.all_customers", "All Customers") }),
    [t]
  );

  const [startDate, setStartDate] = React.useState(null);
  const [endDate, setEndDate] = React.useState(null);
  const [selectedSaleId, setSelectedSaleId] = React.useState("");
  const [selectedCustomerId, setSelectedCustomerId] = React.useState("");
  const [customerSearchTerm, setCustomerSearchTerm] = React.useState("");
  const [selectedCustomerOption, setSelectedCustomerOption] = React.useState(
    allCustomersOption
  );
  const [anchorEl, setAnchorEl] = React.useState(null);
  const [page, setPage] = React.useState(1);
  const [rowsPerPage, setRowsPerPage] = React.useState(10);

  const queryString = React.useMemo(() => {
    const params = [];
    if (startDate) params.push(`start_date=${startDate.format("YYYY-MM-DD")}`);
    if (endDate) params.push(`end_date=${endDate.format("YYYY-MM-DD")}`);
    if (selectedBranch) params.push(`shop=${selectedBranch}`);
    params.push("page_size=all");
    return params.join("&");
  }, [startDate, endDate, selectedBranch]);

  const {
    data: returnsData,
    isLoading,
    isError,
    error,
  } = useFetchObjects(
    ["sale-returns", queryString],
    `shop/sale-returns/?${queryString}`,
    token
  );

  React.useEffect(() => {
    setPage(1);
  }, [startDate, endDate, selectedSaleId, selectedCustomerId]);

  React.useEffect(() => {
    if (!selectedCustomerId && !customerSearchTerm.trim()) {
      setSelectedCustomerOption(allCustomersOption);
    }
  }, [
    allCustomersOption,
    customerSearchTerm,
    selectedCustomerId,
  ]);

  const {
    data: customersData,
    isLoading: customersLoading,
  } = useFetchObjects(
    ["customers", "return-report-filter", customerSearchTerm],
    `shop/customers/?search=${encodeURIComponent(
      customerSearchTerm.trim()
    )}&page_size=20`,
    token
  );

  const returns = returnsData?.results || returnsData || [];

  const customers = Array.isArray(customersData)
    ? customersData
    : customersData?.results || [];

  const dateFilteredReturns = React.useMemo(() => {
    let filtered = returns;

    if (startDate || endDate) {
      filtered = filtered.filter((ret) => {
        const returnDate = ret.return_date ? dayjs(ret.return_date) : null;

        if (!returnDate || !returnDate.isValid()) return false;
        if (startDate && returnDate.isBefore(startDate, "day")) return false;
        if (endDate && returnDate.isAfter(endDate, "day")) return false;

        return true;
      });
    }
    return filtered;
  }, [endDate, returns, startDate]);

  const returnCustomerOptions = React.useMemo(() => {
    return dateFilteredReturns.reduce((acc, ret) => {
      const customerId =
        ret.sale_details?.customer_details?.id ?? ret.sale_details?.customer;
      const customerName = ret.sale_details?.customer_details?.name;

      if (!customerId || !customerName) {
        return acc;
      }

      if (
        !acc.some(
          (customer) => String(customer.id ?? "") === String(customerId)
        )
      ) {
        acc.push({
          id: String(customerId),
          name: customerName,
        });
      }

      return acc;
    }, []);
  }, [dateFilteredReturns]);

  const customerOptions = React.useMemo(() => {
    const mergedCustomers = [...returnCustomerOptions, ...customers].reduce(
      (acc, customer) => {
        const customerId = String(customer?.id ?? "");

        if (!customerId) {
          return acc;
        }

        if (
          !acc.some((existing) => String(existing?.id ?? "") === customerId)
        ) {
          acc.push({
            ...customer,
            id: customerId,
            name: customer?.name || "",
          });
        }

        return acc;
      },
      []
    );

    const options = [allCustomersOption, ...mergedCustomers];

    if (!selectedCustomerOption?.id) {
      return options;
    }

    const alreadyIncluded = options.some(
      (customer) =>
        String(customer?.id ?? "") === String(selectedCustomerOption?.id ?? "")
    );

    return alreadyIncluded
      ? options
      : [selectedCustomerOption, ...options];
  }, [
    allCustomersOption,
    customers,
    returnCustomerOptions,
    selectedCustomerOption,
  ]);

  const saleOptions = React.useMemo(() => {
    return dateFilteredReturns.reduce((acc, ret) => {
      const saleId = ret.sale_details?.id;

      if (
        saleId &&
        !acc.some((option) => option.value === String(saleId))
      ) {
        acc.push({
          value: String(saleId),
          label: `Sale #${saleId}`,
        });
      }

      return acc;
    }, []);
  }, [dateFilteredReturns]);

  const filteredReturns = React.useMemo(() => {
    let filtered = dateFilteredReturns;
    const normalizedCustomerSearch = customerSearchTerm.trim().toLowerCase();

    if (selectedSaleId) {
      filtered = filtered.filter(
        (ret) => String(ret.sale_details?.id ?? "") === selectedSaleId
      );
    }

    if (selectedCustomerId) {
      filtered = filtered.filter(
        (ret) =>
          String(
            ret.sale_details?.customer_details?.id ??
              ret.sale_details?.customer ??
              ""
          ) === String(selectedCustomerId)
      );
    }
    else if (normalizedCustomerSearch) {
      filtered = filtered.filter((ret) =>
        (ret.sale_details?.customer_details?.name || "")
          .toLowerCase()
          .includes(normalizedCustomerSearch)
      );
    }

    return filtered;
  }, [
    customerSearchTerm,
    dateFilteredReturns,
    selectedCustomerId,
    selectedSaleId,
  ]);

  const finalCount = filteredReturns.length;
  const displayReturns = React.useMemo(() => {
    if (rowsPerPage === -1) {
      return filteredReturns;
    }
    const startIndex = (page - 1) * rowsPerPage;
    return filteredReturns.slice(startIndex, startIndex + rowsPerPage);
  }, [filteredReturns, page, rowsPerPage]);

  // Calculate summary totals
  const summaryTotals = React.useMemo(() => {
    return displayReturns.reduce((acc, ret) => {
      const saleTotal = parseFloat(firstCurrencyValue(ret.sale_details?.total_amount)) || 0;
      const refundTotal = parseFloat(ret.total_refound_amount) || 0;
      
      return {
        totalSaleAmount: acc.totalSaleAmount + saleTotal,
        totalRefundAmount: acc.totalRefundAmount + refundTotal
      };
    }, { totalSaleAmount: 0, totalRefundAmount: 0 });
  }, [displayReturns]);

  const open = Boolean(anchorEl);
  const handleClick = (event) => setAnchorEl(event.currentTarget);
  const handleClose = () => setAnchorEl(null);

  const handleExportClick = (type) => {
    const today = new Date().toISOString().slice(0, 10);
    const base = `sale_returns_${today}`;

    if (type === "pdf") exportSaleReturnsToPDF(filteredReturns, `${base}.pdf`);
    handleClose();
  };

  if (isLoading)
    return (
      <Box
        sx={{
          height: "100vh",
          display: "flex",
          justifyContent: "center",
          width: "100%",
        }}
      >
        <LinearProgress sx={{ width: "90%" }} />
      </Box>
    );

  if (isError)
    return (
      <Box
        sx={{
          height: "100vh",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          textAlign: "center",
        }}
      >
        <Typography variant="h6" color="error">
          Error fetching data: {error?.message || 'Unknown error'}
        </Typography>
        <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
          API URL: {process.env.REACT_APP_API_BASE_URL}/shop/sale-returns/
        </Typography>
        <Typography variant="body2" color="textSecondary">
          Token: {token ? 'Present' : 'Missing'}
        </Typography>
        <RefreshIcon 
          sx={{ cursor: "pointer", verticalAlign: "middle", mt: 2 }} 
          onClick={() => window.location.reload()}
        />
      </Box>
    );

  return (
    <>
      <Box sx={{ flexGrow: 1, mb: 5, m: "15px" }}>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={8}>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: { xs: "column", md: "row" },
                  gap: 2,
                  alignItems: { xs: "stretch", md: "center" },
                }}
              >
                <JalaliDatePickerField
                  label={t("ReportIndex.datepicker_start")}
                  value={startDate}
                  onChange={(newValue) => {
                    setStartDate(newValue);
                  }}
                  slotProps={{ textField: { fullWidth: true } }}
                />
                <JalaliDatePickerField
                  label={t("ReportIndex.datepicker_end")}
                  value={endDate}
                  onChange={(newValue) => {
                    setEndDate(newValue);
                  }}
                  slotProps={{ textField: { fullWidth: true } }}
                />
                <FormControl fullWidth>
                  <Select
                    value={selectedSaleId}
                    onChange={(e) => setSelectedSaleId(e.target.value)}
                    displayEmpty
                  >
                    <MenuItem value="">{t("ReportIndex.all_sale_ids")}</MenuItem>
                    {saleOptions.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Autocomplete
                  fullWidth
                  options={customerOptions}
                  value={selectedCustomerOption}
                  inputValue={customerSearchTerm}
                  onChange={(event, newValue) => {
                    const nextOption =
                      newValue && String(newValue.id ?? "") === ""
                        ? allCustomersOption
                        : newValue || allCustomersOption;

                    setSelectedCustomerOption(nextOption);
                    setSelectedCustomerId(
                      nextOption?.id ? String(nextOption.id) : ""
                    );
                    setCustomerSearchTerm(nextOption?.name || "");
                    setPage(1);
                  }}
                  onInputChange={(event, newInputValue, reason) => {
                    if (reason === "input") {
                      setCustomerSearchTerm(newInputValue);
                      if (
                        selectedCustomerId ||
                        selectedCustomerOption?.id === "" ||
                        selectedCustomerOption?.name !== newInputValue
                      ) {
                        setSelectedCustomerId("");
                        setSelectedCustomerOption(null);
                        setPage(1);
                      }
                    }

                    if (reason === "clear") {
                      setCustomerSearchTerm("");
                      setSelectedCustomerId("");
                      setSelectedCustomerOption(allCustomersOption);
                      setPage(1);
                    }
                  }}
                  loading={customersLoading}
                  getOptionLabel={(option) => option?.name || ""}
                  isOptionEqualToValue={(option, value) =>
                    String(option?.id ?? "") === String(value?.id ?? "")
                  }
                  clearOnBlur={false}
                  openOnFocus
                  autoHighlight
                  filterOptions={(options, state) => {
                    const searchValue = state.inputValue.trim().toLowerCase();

                    if (!searchValue) {
                      return options;
                    }

                    return options.filter((option) =>
                      (option?.name || "").toLowerCase().includes(searchValue)
                    );
                  }}
                  loadingText={t("global.loading", "Loading...")}
                  noOptionsText={t(
                    "sale.noCustomersFound",
                    "No customers found"
                  )}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={t("ReportIndex.customer_filter", "Customer")}
                    />
                  )}
                />
                <Button
                  variant="outlined"
                  size="large"
                  onClick={() => {
                    setStartDate(null);
                    setEndDate(null);
                    setSelectedSaleId("");
                    setSelectedCustomerId("");
                    setCustomerSearchTerm("");
                    setSelectedCustomerOption(allCustomersOption);
                    setPage(1);
                  }}
                  sx={{
                    whiteSpace: "nowrap",
                    width: { xs: "100%", md: "auto" },
                    flexShrink: 0,
                    height: 55,
                  }}
                >
                  {t("ReportIndex.btn_clear")}
                </Button>
              </Box>
            </Grid>

            <Grid
              item
              xs={12}
              md={4}
              sx={{
                display: "flex",
                justifyContent: { xs: "flex-start", md: "flex-end" },
              }}
            >
              <FormControl
                sx={{ m: 1, minWidth: 200, width: { xs: "100%", md: "auto" } }}
              >
                <Box>
                  <Button
                    id="export-button"
                    aria-controls={open ? "export-menu" : undefined}
                    aria-haspopup="true"
                    aria-expanded={open ? "true" : undefined}
                    onClick={handleClick}
                    variant="outlined"
                    fullWidth
                    sx={{
                      whiteSpace: "nowrap",
                      width: { xs: "100%", md: "auto" },
                      flexShrink: 0,
                      height: 55,
                    }}
                  >
                    {t("ReportIndex.btn_export")}
                  </Button>
                  <Menu
                    id="export-menu"
                    anchorEl={anchorEl}
                    open={open}
                    onClose={handleClose}
                    MenuListProps={{
                      "aria-labelledby": "export-button",
                    }}
                  >
                    <MenuItem onClick={() => handleExportClick("excel")}>
                      {t("ReportIndex.export_excel")}
                    </MenuItem>
                    <MenuItem onClick={() => handleExportClick("pdf")}>
                      {t("ReportIndex.export_pdf")}
                    </MenuItem>
                    <MenuItem onClick={() => handleExportClick("none")}>
                      {t("ReportIndex.export_none")}
                    </MenuItem>
                  </Menu>
                </Box>
              </FormControl>
            </Grid>
          </Grid>
        </LocalizationProvider>
      </Box>

      <Divider />

      <TableContainer component={Paper}>
        <Table aria-label="sale returns table">
          <TableHead>
            <TableRow>
              <TableCell colSpan={8} style={{ textAlign: "center" }}>
                <Typography variant="h4">
                  {t("ReportIndex.returnSale_tableTitle")}
                </Typography>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableHead>
            <TableRow>
              <TableCell />
              <TableCell>{t("ReportIndex.returnSale_table_saleID", "Sale Reference")}</TableCell>
              <TableCell>{t("ReportIndex.returnSale_table_date", "Return Date")}</TableCell>
              <TableCell>{t("ReportIndex.returnSale_table_reason")}</TableCell>
              <TableCell>
                {t("ReportIndex.returnSale_table_saleTotal")}
              </TableCell>
              <TableCell>
                {t("ReportIndex.returnSale_table_refundTotal")}
              </TableCell>
              <TableCell>
                {t("ReportIndex.returnSale_table_itemCount")}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {displayReturns.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} style={{ textAlign: "center", padding: "20px" }}>
                  <Typography variant="h6" color="textSecondary">
                    {isLoading ? 'Loading...' : 'No return records found'}
                  </Typography>
                  <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                    Total records from API: {returns.length}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Filtered records: {filteredReturns.length}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              displayReturns.map((ret) => (
                <Row key={ret.id} row={ret} />
              ))
            )}
            {/* Summary Row */}
            <TableRow sx={{ backgroundColor: '#f5f5f5', fontWeight: 'bold' }}>
              <TableCell />
              <TableCell />
              <TableCell />
              <TableCell sx={{ fontWeight: 'bold' }}>{t('Summary')}</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>
                {summaryTotals.totalSaleAmount.toFixed(2)}
              </TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>
                {summaryTotals.totalRefundAmount.toFixed(2)}
              </TableCell>
              <TableCell />
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination */}
      <Grid container justifyContent="center" alignItems="center" padding="10px">
        <Box display="flex" alignItems="center">
          <Typography>{t("expense.rowsPerPage", "Rows Per Page")}</Typography>
          <FormControl sx={{ "& .MuiOutlinedInput-root": { "& fieldset": { border: "none" } } }}>
            <Select value={rowsPerPage} onChange={(e) => { setRowsPerPage(Number(e.target.value)); setPage(1); }}>
              <MenuItem value={10}>10</MenuItem>
              <MenuItem value={20}>20</MenuItem>
              <MenuItem value={30}>30</MenuItem>
              <MenuItem value={-1}>{t("reports.all")}</MenuItem>
            </Select>
          </FormControl>
        </Box>
        {rowsPerPage !== -1 && (
          <Pagination
            count={Math.ceil(finalCount / rowsPerPage)}
            page={page}
            onChange={(e, newPage) => setPage(newPage)}
            variant="outlined"
            color="primary"
          />
        )}
      </Grid>
    </>
  );
}
